import { useState } from "react";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";

export default function ContactPage() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError("");
    const form = e.currentTarget;
    const fd = new FormData(form);
    if ((fd.get("website_alt") as string | null)?.trim()) {
      setStatus("success");
      form.reset();
      return;
    }
    fd.delete("website_alt");
    setStatus("loading");
    try {
      const res = await fetch("https://readdy.ai/api/form/danntdlvmcl80jn29acg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(fd as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try { parsed = JSON.parse(text); } catch { /* ignore */ }
      const msg = parsed?.meta?.message || parsed?.meta?.detail || text;
      if (res.ok && parsed?.code === "OK") {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(msg || "Something went wrong. Please try again.");
      }
    } catch {
      setStatus("error");
      setFormError("Network error. Please try again.");
    }
  };

  const inputCls = "mt-2 w-full bg-transparent border-b border-background-200/70 focus:border-foreground-300 py-3 min-h-[48px] text-[15px] md:text-[14px] text-foreground-100 focus:outline-none";
  const labelCls = "text-[11px] tracking-[0.15em] uppercase text-foreground-400";

  return (
    <div className="min-h-screen bg-background-50 text-foreground-100 flex flex-col">
      <SiteHeader variant="solid" />
      <main className="flex-1 pt-[72px] md:pt-24">
        <section className="w-full px-5 md:px-10 py-10 md:py-20">
          <div className="max-w-[1200px] mx-auto flex flex-col lg:flex-row gap-12 lg:gap-16">
            {/* Left — 38% */}
            <div className="w-full lg:w-[38%]">
              <p className="text-[10px] tracking-[0.24em] uppercase text-foreground-400 mb-3">Contact</p>
              <h1 className="text-[32px] md:text-[36px] leading-[1.08] tracking-[-0.01em] text-foreground-50 font-light mb-5 md:mb-6">
                Talk to us — a real person answers.
              </h1>
              <p className="text-[16px] md:text-[14px] text-foreground-300 leading-relaxed mb-8 max-w-sm">
                Product questions, returns, warranty, or a note from the trail — send it over. We reply within one business day.
              </p>
              <ul className="space-y-4 text-[13px] text-foreground-200">
                <li>
                  <p className={labelCls}>Email</p>
                  <p className="mt-1 text-foreground-100">hello@northvale.co</p>
                </li>
                <li>
                  <p className={labelCls}>Location</p>
                  <p className="mt-1 text-foreground-100">Bergen, Norway</p>
                </li>
                <li>
                  <p className={labelCls}>Service hours</p>
                  <p className="mt-1 text-foreground-100">Mon–Fri · 09:00–17:00 CET</p>
                </li>
              </ul>
            </div>

            {/* Right — 62% */}
            <div className="w-full lg:w-[62%]">
              <form onSubmit={handleSubmit} data-readdy-form id="contact-form" className="space-y-7 md:space-y-6">
                <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="hp-field" />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
                  <label className="block">
                    <span className={labelCls}>Full name</span>
                    <input name="name" required maxLength={80} className={inputCls} />
                  </label>
                  <label className="block">
                    <span className={labelCls}>Email</span>
                    <input type="email" name="email" required maxLength={200} className={inputCls} />
                  </label>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
                  <label className="block">
                    <span className={labelCls}>Subject</span>
                    <select name="subject" required className={inputCls}>
                      <option value="general" className="bg-background-50">General question</option>
                      <option value="order" className="bg-background-50">Order support</option>
                      <option value="warranty" className="bg-background-50">Warranty / repair</option>
                      <option value="press" className="bg-background-50">Press / partnerships</option>
                    </select>
                  </label>
                  <label className="block">
                    <span className={labelCls}>Order number <span className="text-foreground-500 normal-case tracking-normal">(optional)</span></span>
                    <input name="order_number" maxLength={40} className={inputCls} />
                  </label>
                </div>

                <label className="block">
                  <span className={labelCls}>Message</span>
                  <textarea
                    name="message"
                    required
                    maxLength={500}
                    className="mt-2 w-full min-h-[160px] md:min-h-[200px] bg-transparent border border-background-200/70 focus:border-foreground-300 p-4 text-[15px] md:text-[14px] text-foreground-100 focus:outline-none resize-y"
                  />
                </label>

                <div className="pt-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <button
                    type="submit"
                    disabled={status === "loading"}
                    className="inline-flex items-center gap-2 px-8 py-4 bg-primary-500 hover:bg-primary-600 text-foreground-50 text-[12px] tracking-[0.18em] uppercase transition cursor-pointer whitespace-nowrap disabled:opacity-60"
                  >
                    {status === "loading" ? "Sending…" : "Send message"}
                    <i className="ri-arrow-right-line"></i>
                  </button>
                  <div className="min-h-[20px] text-xs">
                    {status === "success" && <span className="text-foreground-300">Thanks — we'll be in touch shortly.</span>}
                    {status === "error" && <span className="text-primary-400">{formError}</span>}
                  </div>
                </div>
              </form>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}